import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { referralsAPI } from '../api';
import {
  ChevronLeft, Gift, Share2, Copy, Check, Users, TrendingUp,
  Tag, Clock, Sparkles, MessageCircle, ArrowRight
} from 'lucide-react';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';
const GREEN = '#166534';

const Referrals = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    let mounted = true;
    (async () => {
      try {
        const [m, h] = await Promise.all([referralsAPI.me(), referralsAPI.history()]);
        if (mounted) { setData(m); setHistory(h); }
      } catch {}
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [user, authLoading, navigate]);

  const copyCode = async () => {
    if (!data?.referral_code) return;
    try {
      await navigator.clipboard.writeText(data.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const shareWhatsApp = () => {
    if (data?.whatsapp_url) window.open(data.whatsapp_url, '_blank');
  };

  const shareNative = async () => {
    if (!data) return;
    if (navigator.share) {
      try { await navigator.share({ title: "Lunora's", text: data.share_text, url: data.share_url }); }
      catch {}
    } else {
      shareWhatsApp();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: SOFT }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: GOLD, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!data) return null;
  const s = data.settings;

  return (
    <div data-testid="referrals-page" className="min-h-screen" style={{ backgroundColor: SOFT, fontFamily: 'Outfit, sans-serif' }}>
      {/* Header */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-4 py-4 border-b"
        style={{ backgroundColor: 'rgba(250,249,246,0.9)', backdropFilter: 'blur(12px)', borderColor: LINE }}>
        <button data-testid="back-btn" onClick={() => navigate('/landing')} className="p-2 -ml-2">
          <ChevronLeft className="w-6 h-6" style={{ color: INK }} strokeWidth={1.5} />
        </button>
        <h1 className="text-lg" style={{ fontFamily: 'Playfair Display, serif', color: INK }}>Refer & Earn</h1>
        <div className="w-10" />
      </div>

      {/* Hero card */}
      <div className="px-4 pt-5">
        <div className="relative p-5 text-white overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0F0F0F 0%, #1A1A1A 60%, #2B1A0F 100%)' }}>
          <div className="absolute top-0 right-0 w-40 h-40 opacity-20"
            style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)` }} />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4" style={{ color: GOLD }} strokeWidth={1.5} />
              <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: GOLD }}>Invite & Earn</p>
            </div>
            <h2 className="text-[26px] leading-tight mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
              Give ₹{s.referee_reward}, Get ₹{s.referrer_reward}
            </h2>
            <p className="text-[12px] opacity-80 mb-5" style={{ lineHeight: 1.5 }}>
              Your friend gets ₹{s.referee_reward} off their first order. You get ₹{s.referrer_reward} when they pay advance.
            </p>

            {/* Code card */}
            <div className="bg-white/10 backdrop-blur border border-white/20 p-3 mb-3">
              <p className="text-[9px] tracking-[0.18em] uppercase opacity-70 mb-1">Your Code</p>
              <div className="flex items-center justify-between gap-2">
                <p data-testid="my-referral-code" className="text-[22px] tracking-[0.08em]" style={{ fontFamily: 'Playfair Display, serif', color: GOLD }}>
                  {data.referral_code}
                </p>
                <button data-testid="copy-code-btn" onClick={copyCode}
                  className="px-3 py-1.5 text-[10px] tracking-[0.08em] uppercase bg-white/20 flex items-center gap-1.5 hover:bg-white/30 transition">
                  {copied ? <Check className="w-3 h-3" strokeWidth={2} /> : <Copy className="w-3 h-3" strokeWidth={1.5} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Share buttons */}
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="share-whatsapp-btn" onClick={shareWhatsApp}
                className="flex items-center justify-center gap-2 py-3 text-[11px] tracking-[0.08em] uppercase"
                style={{ backgroundColor: '#25D366', color: '#FFF', fontWeight: 500 }}>
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                WhatsApp
              </button>
              <button data-testid="share-more-btn" onClick={shareNative}
                className="flex items-center justify-center gap-2 py-3 text-[11px] tracking-[0.08em] uppercase"
                style={{ backgroundColor: GOLD, color: INK, fontWeight: 500 }}>
                <Share2 className="w-4 h-4" strokeWidth={1.5} />
                More
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 mt-4 grid grid-cols-3 gap-2">
        <StatCard icon={Users} label="Invited" value={data.stats.friends_joined} color={GOLD} />
        <StatCard icon={Check} label="Rewarded" value={data.stats.friends_rewarded} color={GREEN} />
        <StatCard icon={TrendingUp} label="Earned" value={`₹${data.stats.total_rewards_earned}`} color="#991B1B" />
      </div>

      {/* Tabs */}
      <div className="px-4 mt-5 flex gap-1 border-b" style={{ borderColor: LINE }}>
        {[
          { id: 'overview', label: 'How It Works' },
          { id: 'friends', label: `Friends (${history?.referrals?.length || 0})` },
          { id: 'coupons', label: `Coupons (${history?.coupons?.length || 0})` },
        ].map(t => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className="px-3 py-2 text-[11px] tracking-[0.06em] uppercase border-b-2 transition"
            style={{ borderColor: tab === t.id ? INK : 'transparent', color: tab === t.id ? INK : MUTED, fontWeight: tab === t.id ? 500 : 400 }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-4 py-4 pb-10">
        {tab === 'overview' && (
          <div className="space-y-3">
            {[
              { num: '01', title: 'Share your code', desc: `Send ${data.referral_code} to friends via WhatsApp or any app.` },
              { num: '02', title: 'Friend signs up', desc: `They use your code at signup and get ₹${s.referee_reward} off instantly.` },
              { num: '03', title: 'Friend places first order', desc: `After they pay their 50% advance, you get ₹${s.referrer_reward} credit.` },
              { num: '04', title: 'Redeem at checkout', desc: `Apply your coupon on any order of ₹${s.min_order}+. Valid ${s.validity_days} days.` },
            ].map(step => (
              <div key={step.num} className="bg-white border p-3 flex gap-3" style={{ borderColor: LINE }}>
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-[14px]"
                  style={{ backgroundColor: SOFT, color: GOLD, fontFamily: 'Playfair Display, serif' }}>
                  {step.num}
                </div>
                <div>
                  <p className="text-[13px] mb-0.5" style={{ color: INK, fontWeight: 500 }}>{step.title}</p>
                  <p className="text-[11px]" style={{ color: MUTED, lineHeight: 1.5 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'friends' && (
          <div className="space-y-2">
            {(!history?.referrals || history.referrals.length === 0) ? (
              <EmptyState icon={Users} title="No friends invited yet" sub="Share your code to start earning rewards." />
            ) : history.referrals.map(r => (
              <div key={r.id} data-testid={`referral-${r.id}`} className="bg-white border p-3 flex items-center justify-between" style={{ borderColor: LINE }}>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px]" style={{ color: INK, fontWeight: 500 }}>
                    {r.referee_name || 'Anonymous Friend'}
                  </p>
                  <p className="text-[10px]" style={{ color: MUTED }}>
                    Joined {new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </p>
                </div>
                <span className="text-[9px] px-2 py-1 flex items-center gap-1"
                  style={{
                    backgroundColor: r.status === 'rewarded' ? 'rgba(22,101,52,0.08)' : 'rgba(212,175,55,0.08)',
                    color: r.status === 'rewarded' ? GREEN : GOLD,
                  }}>
                  {r.status === 'rewarded' ? <><Check className="w-2.5 h-2.5" strokeWidth={2} />Reward earned</> :
                    <><Clock className="w-2.5 h-2.5" strokeWidth={1.5} />Awaiting 1st order</>}
                </span>
              </div>
            ))}
          </div>
        )}

        {tab === 'coupons' && (
          <div className="space-y-2">
            {(!history?.coupons || history.coupons.length === 0) ? (
              <EmptyState icon={Tag} title="No coupons yet" sub="Earn ₹200 when your referred friends place their first order." />
            ) : history.coupons.map(c => {
              const isActive = c.status === 'active';
              const isExpiringSoon = isActive && new Date(c.expires_at) < new Date(Date.now() + 7*24*3600*1000);
              return (
                <div key={c.id} data-testid={`coupon-${c.code}`} className="bg-white border-2 overflow-hidden"
                  style={{ borderColor: isActive ? GOLD : LINE, borderStyle: isActive ? 'solid' : 'dashed' }}>
                  <div className="flex">
                    <div className="flex-shrink-0 w-16 flex items-center justify-center py-4"
                      style={{ backgroundColor: isActive ? GOLD : LINE }}>
                      <Gift className="w-6 h-6" style={{ color: isActive ? INK : MUTED }} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 px-3 py-3">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <p className="text-[16px]" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
                          ₹{c.discount_amount} OFF
                        </p>
                        <span className="text-[9px] px-1.5 py-0.5"
                          style={{
                            backgroundColor: c.status === 'used' ? LINE : c.status === 'expired' ? 'rgba(153,27,27,0.08)' : 'rgba(22,101,52,0.08)',
                            color: c.status === 'used' ? MUTED : c.status === 'expired' ? '#991B1B' : GREEN,
                          }}>
                          {c.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-[10px] font-mono tracking-wider mb-1" style={{ color: GOLD, fontWeight: 600 }}>{c.code}</p>
                      <p className="text-[10px]" style={{ color: MUTED }}>
                        Min order ₹{c.min_order} · {isActive ? (isExpiringSoon ? 'Expires soon! ' : 'Valid till ') : 'Expired '}
                        {new Date(c.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating CTA (mobile) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 flex gap-2" style={{ borderColor: LINE, boxShadow: '0 -4px 12px rgba(0,0,0,0.04)' }}>
        <button onClick={() => navigate('/category-selection')}
          className="flex-1 py-3 text-[11px] tracking-[0.08em] uppercase border" style={{ borderColor: INK, color: INK }}>
          Shop Now
        </button>
        <button data-testid="cta-share-btn" onClick={shareWhatsApp}
          className="flex-1 py-3 text-[11px] tracking-[0.08em] uppercase text-white flex items-center justify-center gap-1.5"
          style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}>
          Invite Friend <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border p-3" style={{ borderColor: LINE }}>
    <Icon className="w-4 h-4 mb-1" style={{ color }} strokeWidth={1.5} />
    <p className="text-[9px] tracking-[0.1em] uppercase mb-0.5" style={{ color: MUTED }}>{label}</p>
    <p className="text-[18px]" style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>{value}</p>
  </div>
);

const EmptyState = ({ icon: Icon, title, sub }) => (
  <div className="py-10 flex flex-col items-center text-center">
    <Icon className="w-10 h-10 mb-2" style={{ color: '#BFBFBF' }} strokeWidth={1.2} />
    <p className="text-[13px] mb-1" style={{ color: INK, fontWeight: 500 }}>{title}</p>
    <p className="text-[11px]" style={{ color: MUTED }}>{sub}</p>
  </div>
);

export default Referrals;
